import moment from "moment-timezone";
import { Item } from "lib/features/item/types";

export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const businessColors = [
  { bg: "#FFB59C80", text: "#0E4D2E", border: "#E3967C" },
  { bg: "#B2F7EF80", text: "#0E4D2E", border: "#7FE3DA" },
  { bg: "#C7F46480", text: "#0E4D2E", border: "#A3DA40" },
  { bg: "#BEE5A080", text: "#0E4D2E", border: "#A0C884" },
  { bg: "#6FCF9780", text: "#0E4D2E", border: "#54B67C" },
  { bg: "#A8E6CF80", text: "#0E4D2E", border: "#83D5B6" },
  { bg: "#CDB4DB80", text: "#0E4D2E", border: "#A78EB8" },
  { bg: "#A2D2FF80", text: "#0E4D2E", border: "#7CB8F2" },
  { bg: "#B0BEC580", text: "#0E4D2E", border: "#8E9EA6" },
];

export const getBusinessColor = () => {
  return { bg: "#A8E6CF80", text: "#0E4D2E" };
};

export const getBusinessColorByName = (name: string | undefined | null) => {
  if (!name) {
    return { bg: "#f5f5f5", text: "#666", border: "" };
  }
  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return businessColors[hash % businessColors.length];
};

export const formatTimeShort = (dateString: string) => {
  try {
    return moment.utc(dateString).tz("America/New_York").format("h:mm A");
  } catch (error) {
    console.error("Error formatting time:", error);
    return dateString;
  }
};

export const getDuration = (startTime: string, endTime: string): string => {
  try {
    const start = moment.utc(startTime).tz("America/New_York");
    const end = moment.utc(endTime).tz("America/New_York");

    let duration = moment.duration(end.diff(start)).asHours();

    if (duration < 0) {
      const correctedEnd = moment
        .utc(endTime)
        .tz("America/New_York")
        .add(1, "day");
      duration = moment.duration(correctedEnd.diff(start)).asHours();
    }

    if (duration > 12) duration -= 12;

    return `${duration} hr`;
  } catch (error) {
    console.error("Error calculating duration:", error);
    return "";
  }
};

export const getBusinessName = (item: Item): string => {
  if (item.item_type === 4 && item.farehabor_item_details?.name) {
    return item.farehabor_item_details.name;
  } else if (item.itemDetails?.[0]?.item_name) {
    return item.itemDetails[0].item_name;
  } else if (item.business?.name) {
    return item.business.name;
  }
  return "Unknown Business";
};

export const getPriceForSlot = (item: Item, availability: any): number => {
  let price = 0;

  if (availability) {
    if (availability.customer_type_rates?.[0]?.customer_prototype?.total) {
      price =
        Number(availability.customer_type_rates[0].customer_prototype.total) /
        100;
    } else if (availability.price) {
      price = Number(availability.price) / 100;
    }

    if (
      item?.fareharbor_availabilities &&
      availability.custom_field_instances
    ) {
      try {
        const extendedOption = availability.custom_field_instances.find(
          (f: any) =>
            f.custom_field.type === "extended-option" &&
            f.custom_field.is_required,
        );
        if (extendedOption)
          price = extendedOption.custom_field.extended_options[0].total / 100;
      } catch {}
    }
  } else {
    price = item.price || 0;
  }

  return price;
};

export const getAvailableCount = (item: Item, availability: any): number => {
  if (availability) return availability.capacity || availability.available || 1;
  return item.capacity || item.available || 1;
};

export const getAvailableSpots = (
  item: Item,
  availability: any,
  bookingCount: number,
): number => {
  const totalCapacity = getAvailableCount(item, availability);
  return Math.max(0, totalCapacity - bookingCount);
};

export const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const extractBusinessNames = (items: Item[]): string[] => {
  const names = new Set<string>();

  items.forEach((item) => {
    const businessName = getBusinessName(item);
    if (businessName && businessName !== "Unknown Business") {
      names.add(businessName);
    }
  });

  return Array.from(names).sort();
};
